import React, { useEffect, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import AsyncPaginate from "react-select-async-paginate";
import get from "lodash/get";

import { api, queryBuilder } from "services";

const load = async (
	search,
	prevOptions,
	{ page },
	url,
	filterParams,
	loadOptionsParams,
	loadOptionsKey
) => {
	const { data } = await api.request.get(
		queryBuilder(url, {
			page,
			filter: filterParams,
			...loadOptionsParams(search)
		})
	);

	return {
		options: loadOptionsKey
			? typeof loadOptionsKey === "function"
				? loadOptionsKey(data)
				: get(data, loadOptionsKey, [])
			: data,
		hasMore: get(data, "current_page", 1) < get(data, "last_page", 1),
		additional: { page: get(data, "current_page", 1) + 1 }
	};
};

const AsyncSelectS1 = ({
	disableOptions,
	className,
	label,
	isMulti,
	loadOptionsKey,
	placeholder,
	options,
	field,
	optionLabel,
	optionValue,
	size,
	form: { errors, setFieldValue, setFieldTouched, touched },
	isDisabled,
	isSearchable,
	menuPlacement,
	loadOptionsUrl,
	isRequired,
	isClearable = false,
	loadOptionsParams = () => { },
	handleChange = () => { },
	filterParams = {},
	canUpdate
}) => {
	const classes = cx(
		"form-field",
		touched[field.name] && errors[field.name] && "form-field--error",
		field.value && "form-field--active",
		className
	);

	const getSizeHeight = () => {
		switch (size) {
			case "default":
				return "48px";
			case "small":
				return "36px";
			case "large":
				return "58px";
			default:
				return "48px";
		}
	};
	const getSizeRadius = () => {
		switch (size) {
			case "default":
				return "10px";
			case "small":
				return "5px";
			case "large":
				return "10px";
			default:
				return "10px";
		}
	};

	const getSizeFont = () => {
		switch (size) {
			case "default":
				return "16px";
			case "small":
				return "14px";
			case "large":
				return "18px";
			default:
				return "16px";
		}
	};

	const customStyle = {
		control: provided => ({
			...provided,
			cursor: "pointer",
			borderColor: "rgba(0,0,0,.1)",
			border:
				touched[field.name] && errors[field.name]
					? "1px solid red"
					: "1px solid #e1e2e6",
			minHeight: getSizeHeight(),
			borderRadius: getSizeRadius()
		}),
		container: provided => ({
			...provided,
			minHeight: getSizeHeight(),
			minWidth: "150px"
		}),
		indicatorSeparator: provided => ({
			...provided,
			display: "none"
		}),
		indicatorsContainer: provided => ({
			...provided,
			height: getSizeHeight()
		}),
		multiValue: base => ({
			...base,
			backgroundColor: "#d2daec"
		}),
		option: provided => ({
			...provided,
			fontSize: getSizeFont(),
			fontFamily: "Inter"
		}),
		placeholder: provided => ({
			...provided,
			fontSize: getSizeFont(),
			fontFamily: "Inter",
			whiteSpace: "nowrap",
			color: "#bfbfbf",
			overflow: "hidden",
			textOverflow: "ellipsis"
		})
	};

	const [isVisible, setVisible] = useState(true);

	useEffect(() => {
		setVisible(prev => !prev);
		setTimeout(() => {
			setVisible(prev => !prev);
		}, 0);
	}, [canUpdate]);

	return (
		<div className={classes}>
			{label && (
				<label className="form-field__label">
					{label}
					{isRequired && <span title={"Обязательное поле"}>*</span>}
				</label>
			)}
			{isVisible ? (
				<div className="pos-relative">
					<AsyncPaginate
						isDisabled={isDisabled}
						id={field.name}
						name={field.name}
						debounceTimeout={300}
						onChange={option => {
							setFieldValue(field.name, option);
							if (handleChange) {
								handleChange(option);
							}
						}}
						onBlur={() => setFieldTouched(field.name, true)}
						getValue={option => option[optionValue]}
						getOptionLabel={option =>
							typeof optionLabel === "function"
								? optionLabel(option)
								: option[optionLabel]
						}
						getOptionValue={option =>
							typeof optionValue === "function"
								? optionValue(option)
								: option[optionValue]
						}
						value={field.value}
						styles={customStyle}
						isClearable={isClearable}
						additional={{ page: 1 }}
						loadOptions={(search, prevOptions, { page }) =>
							load(
								search,
								prevOptions,
								{ page },
								loadOptionsUrl,
								filterParams,
								loadOptionsParams,
								loadOptionsKey
							)
						}
						isOptionDisabled={option =>
							disableOptions
								.reduce((prev, curr) => [...prev, curr.id], [])
								.includes(option.id)
						}
						{...{ isMulti, options, placeholder, isSearchable, menuPlacement }}
					/>

					{touched[field.name] && errors[field.name] && (
						<div className="form-field__error">
							<span>{errors[field.name]}</span>
						</div>
					)}
				</div>
			) : (
				<div
					className="pos-relative"
					style={{ minHeight: getSizeHeight() }}
				></div>
			)}
		</div>
	);
};

AsyncSelectS1.propTypes = {
	title: PropTypes.string.isRequired,
	className: PropTypes.string,
	optionValue: PropTypes.string,
	optionLabel: PropTypes.string,
	isSearchable: PropTypes.bool,
	isDisabled: PropTypes.bool,
	menuPlacement: PropTypes.string,
	size: PropTypes.oneOf(["default", "large", "small"]),
	loadOptionsKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
		.isRequired
};

AsyncSelectS1.defaultProps = {
	title: "",
	className: null,
	optionValue: "id",
	optionLabel: "title",
	isSearchable: false,
	isDisabled: false,
	menuPlacement: "bottom",
	disableOptions: [],
	loadOptionsKey: "data",
	size: "default"
};

export default AsyncSelectS1;
